import { useEffect, useState, useCallback } from "react";
import { useBusiness } from "@/context/BusinessContext";
import { apiAdmin } from "@/lib/api";
import { resolveActivePlaylist } from "@/lib/schedule";
import TVLivePreview from "@/components/TVLivePreview";
import { Maximize2, Minimize2, Grid3x3, Tv } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * NOC Mosaic — panoramic grid of all TVs for at-a-glance monitoring.
 * Reuses the existing TVLivePreview component (already optimized for many tiles).
 */
export default function NOCPage() {
  const { activeBusiness } = useBusiness();
  const [tvs, setTvs] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [media, setMedia] = useState([]);
  const [nowPlaying, setNowPlaying] = useState({});
  const [cols, setCols] = useState(3); // 2/3/4 columns
  const [fullscreen, setFullscreen] = useState(false);
  const [, setNowTick] = useState(0); // forces re-render every 60s so schedule resolution stays live

  useEffect(() => {
    // One minute is enough — schedule windows are minute-granular. Was 30s, halved work.
    const id = setInterval(() => setNowTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    if (!activeBusiness) return;
    const [tv, pl, md] = await Promise.all([
      apiAdmin.get(`/tvs?business_id=${activeBusiness.id}`),
      apiAdmin.get(`/playlists?business_id=${activeBusiness.id}`),
      apiAdmin.get(`/media?business_id=${activeBusiness.id}`),
    ]);
    setTvs(tv.data);
    setPlaylists(pl.data);
    setMedia(md.data);
  }, [activeBusiness]);

  useEffect(() => { load(); }, [load]);

  // Live status polling — bulk endpoint for all TVs
  useEffect(() => {
    if (!activeBusiness) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await apiAdmin.get(`/tvs/now-playing-bulk?business_id=${activeBusiness.id}`);
        if (cancelled) return;
        const next = {};
        data.forEach((it) => { next[it.tv_id] = it; });
        setNowPlaying(next);
      } catch {}
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [activeBusiness]);

  const online = tvs.filter((t) => nowPlaying[t.id]?.status === "online").length;

  const gridClass =
    cols === 2 ? "grid-cols-1 md:grid-cols-2"
    : cols === 4 ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
    : "grid-cols-2 md:grid-cols-3";

  return (
    <div
      className={fullscreen ? "fixed inset-0 z-50 bg-black p-6 overflow-auto" : ""}
      data-testid="noc-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1
            className="font-heading text-3xl font-semibold"
            style={{ color: fullscreen ? "#FFFFFF" : "#1D1D1F", letterSpacing: "-0.02em" }}
            data-testid="noc-title"
          >
            NOC · Wall of TVs
          </h1>
          <p className="text-sm mt-1" style={{ color: fullscreen ? "#A1A1A6" : "#6E6E73" }}>
            {tvs.length} pantallas · <span className="text-emerald-500 font-medium">{online} online</span>
            {" "}· actualiza en vivo cada 5s
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="flex rounded-lg overflow-hidden border"
            style={{ borderColor: fullscreen ? "#1D1D1F" : "#D2D2D7" }}
            data-testid="noc-cols-toggle"
          >
            {[2, 3, 4].map((n) => (
              <button
                key={n}
                onClick={() => setCols(n)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  cols === n
                    ? "bg-blue-600 text-white"
                    : fullscreen ? "text-white hover:bg-white/10" : "text-slate-600 hover:bg-slate-100"
                }`}
                data-testid={`noc-cols-${n}`}
              >
                <Grid3x3 className="h-3.5 w-3.5 inline mr-1" />{n}×
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFullscreen((f) => !f)}
            className={fullscreen ? "border-slate-700 bg-transparent text-white hover:bg-white/10" : ""}
            data-testid="noc-fullscreen-toggle"
          >
            {fullscreen ? <><Minimize2 className="h-3.5 w-3.5 mr-1.5"/>Salir</> : <><Maximize2 className="h-3.5 w-3.5 mr-1.5"/>Pantalla completa</>}
          </Button>
        </div>
      </div>

      {/* Grid */}
      {tvs.length === 0 ? (
        <EmptyState dark={fullscreen} />
      ) : (
        <div className={`grid gap-4 ${gridClass}`} data-testid="noc-grid">
          {tvs.map((tv) => {
            const np = nowPlaying[tv.id];
            // Prefer the server-resolved active playlist (truth source). Falls
            // back to client-side resolver if the bulk endpoint hasn't responded yet.
            const playlist =
              (np?.active_playlist_id &&
                playlists.find((p) => p.id === np.active_playlist_id)) ||
              resolveActivePlaylist(tv, playlists);
            const isOnline = np?.status === "online";
            return (
              <div
                key={tv.id}
                className="relative aspect-video rounded-xl overflow-hidden bg-black ring-1 ring-white/5 group"
                data-testid={`noc-tile-${tv.id}`}
              >
                <div className="absolute inset-0">
                  <TVLivePreview playlist={playlist} media={media} nowPlaying={np} />
                </div>
                {/* Overlay header */}
                <div className="absolute top-0 inset-x-0 px-3 py-2 bg-gradient-to-b from-black/70 to-transparent flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`h-2 w-2 rounded-full ${isOnline ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`}
                      data-testid={`noc-status-${tv.id}`}
                    />
                    <div className="text-xs font-medium text-white truncate">{tv.name}</div>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-white/70 shrink-0">
                    {tv.location}
                  </div>
                </div>
                {/* Bottom info */}
                <div className="absolute bottom-0 inset-x-0 px-3 py-1.5 bg-gradient-to-t from-black/70 to-transparent">
                  <div className="text-[10px] text-white/80 truncate flex items-center gap-1.5">
                    {playlist ? (
                      <>
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="truncate">{playlist.name}</span>
                      </>
                    ) : (tv.schedules?.length || 0) > 0 ? (
                      <span className="text-white/60 italic">Idle · fuera de horario</span>
                    ) : (
                      <span className="text-white/60 italic">Sin schedule</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EmptyState({ dark }) {
  return (
    <div
      className="rounded-2xl border border-dashed p-16 text-center"
      style={{
        borderColor: dark ? "#1D1D1F" : "#D2D2D7",
        background: dark ? "transparent" : "#FFFFFF",
      }}
    >
      <Tv className="h-10 w-10 mx-auto mb-3" style={{ color: dark ? "#6E6E73" : "#A1A1A6" }} />
      <div className="font-heading text-lg font-semibold" style={{ color: dark ? "#FFFFFF" : "#1D1D1F" }}>
        No hay TVs todavía
      </div>
      <p className="text-sm mt-1" style={{ color: dark ? "#A1A1A6" : "#6E6E73" }}>
        Crea Fire Sticks desde la sección Televisions para verlos aquí.
      </p>
    </div>
  );
}
